import React, { useEffect, useState } from "react";
import { auth } from "./firebase";
import { motion } from "framer-motion";
import YocoButton from "./YocoButton";

const Pricing = () => {
  const user = auth.currentUser;
  const [currency, setCurrency] = useState("ZAR");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [loading, setLoading] = useState(true);

  const currencySymbols = { ZAR: "R", USD: "$", EUR: "€", GBP: "£" };
  const currencyEmojis = { ZAR: "🇿🇦", USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧" };
  const supportedCurrencies = ["ZAR", "USD", "EUR", "GBP"];

  const plans = [
    { id: "starter", name: "Starter", amount: 3799, description: "1 Investor Contact", contacts: 1 },
    { id: "pro", name: "Pro", amount: 8999, description: "3 Investor Contacts", contacts: 3, popular: true },
    { id: "unlimited", name: "Unlimited", amount: 88999, description: "Unlimited Investor Contacts", contacts: 99, bestValue: true },
  ];

  // Detect user currency
  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then(res => res.json())
      .then(data => {
        const detectedCurrency = data.currency || "ZAR";
        const curr = supportedCurrencies.includes(detectedCurrency) ? detectedCurrency : "USD";
        setCurrency(curr);
        updateExchangeRate(curr);
      })
      .catch(() => {
        setCurrency("ZAR");
        updateExchangeRate("ZAR");
      });
  }, []);

  const updateExchangeRate = (targetCurrency) => {
    setLoading(true);
    fetch(`https://api.exchangerate.host/latest?base=ZAR&symbols=${targetCurrency}`)
      .then(r => r.json())
      .then(rateData => {
        setExchangeRate(rateData.rates?.[targetCurrency] || 1);
        setLoading(false);
      })
      .catch(() => {
        setExchangeRate(1);
        setLoading(false);
      });
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen text-xl">🌍 Loading currency rates...</div>;

  return (
    <div className="flex flex-col items-center p-6 bg-gray-50 min-h-screen">
      <h1 className="text-4xl font-bold mb-6">💰 Choose Your Plan</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-5xl">
        {plans.map(plan => {
          const convertedAmount = Math.round(plan.amount * exchangeRate);

          const badge = plan.popular
            ? { text: "🔥 Most Popular", color: "from-green-500 to-green-400", glow: "rgba(34,197,94,0.6)" }
            : plan.bestValue
            ? { text: "⭐ Best Value", color: "from-yellow-400 to-yellow-300", glow: "rgba(250,204,21,0.6)" }
            : null;

          return (
            <motion.div
              key={plan.id}
              className={`bg-white p-6 rounded-xl shadow-lg flex flex-col items-center relative ${
                plan.popular ? "border-4 border-green-500" : plan.bestValue ? "border-4 border-yellow-400" : ""
              }`}
              whileHover={{ scale: 1.05, boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              {badge && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  className={`absolute -top-4 bg-gradient-to-r ${badge.color} text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg`}
                  style={{ boxShadow: `0 0 15px ${badge.glow}` }}
                >
                  {badge.text}
                </motion.span>
              )}

              <h2 className="text-2xl font-semibold mb-2">{plan.name}</h2>
              <p className="text-gray-600 mb-2">{plan.description}</p>

              <motion.p
                initial={{ y: -5 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.4, type: "spring" }}
                className="text-3xl font-bold mb-6"
              >
                {currencyEmojis[currency]} {currencySymbols[currency]}
                {convertedAmount}
              </motion.p>

              <YocoButton
                planId={plan.id}
                description={`${plan.name} - ${plan.description} (${currencySymbols[currency]}${convertedAmount})`}
                onSuccess={() => console.log(`${plan.name} plan purchased!`)}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Pricing;
